import { faChevronDown, faChevronUp } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import Flex from 'components/ui/Flex';
import Text from 'components/ui/Text';
import useAppSelector from 'hooks/useAppSelector';
import { useTranslation } from 'react-i18next';
import { orderSelectors } from 'store/reducers/orderSlice';
import { OrderDeliveryInfo } from 'returnCenter-api';
import { styled } from 'styled-components';

const OrderInfoCard = () => {
    const orderDeliveryInfo = useAppSelector(orderSelectors.selectOrderDeliveryInfo);
    const { t } = useTranslation();

    // First order is expanded by default
    const [expandedOrders, setExpandedOrders] = useState<Set<number>>(new Set([0]));

    const hasData = orderDeliveryInfo && orderDeliveryInfo.length > 0;

    const formatDate = (date?: string | Date | null) => {
        if (!date) return '';
        return new Date(date).toLocaleDateString();
    };

    const toggleOrder = (index: number) => {
        setExpandedOrders((prev) => {
            const next = new Set(prev);
            if (next.has(index)) {
                next.delete(index);
            } else {
                next.add(index);
            }
            return next;
        });
    };

    const hasDetails = (order: OrderDeliveryInfo): boolean => {
        return Boolean(order.reference || order.customerOrderReference || order.goodsLabel || order.deliveryAddress);
    };

    const formatAddress = (address: OrderDeliveryInfo['deliveryAddress']) => {
        if (!address) return null;
        const parts = [
            address.name,
            address.street,
            [address.zipCode, address.city].filter(Boolean).join(' '),
            address.country,
        ].filter(Boolean);
        return parts;
    };

    const renderOrderDetails = (order: OrderDeliveryInfo) => {
        const addressParts = formatAddress(order.deliveryAddress);

        return (
            <DetailsWrapper column>
                {order.reference && (
                    <StyledText as="label" bold>
                        {t('reference')}:{' '}
                        <StyledText thin as="span">
                            {order.reference}
                        </StyledText>
                    </StyledText>
                )}
                {order.customerOrderReference && (
                    <StyledText as="label" bold>
                        {t('orderReference')}:{' '}
                        <StyledText thin as="span">
                            {order.customerOrderReference}
                        </StyledText>
                    </StyledText>
                )}
                {order.goodsLabel && (
                    <StyledText as="label" bold>
                        {t('goodsLabel')}:{' '}
                        <StyledText thin as="span">
                            {order.goodsLabel}
                        </StyledText>
                    </StyledText>
                )}
                {order.orderType && (
                    <StyledText as="label" bold>
                        {t('orderType')}:{' '}
                        <StyledText thin as="span">
                            {order.orderType}
                        </StyledText>
                    </StyledText>
                )}
                {addressParts && addressParts.length > 0 && (
                    <AddressBlock column>
                        <StyledText as="label" bold>
                            {t('deliveryAddress')}:
                        </StyledText>
                        {addressParts.map((line, idx) => (
                            <StyledText thin as="span" key={idx}>
                                {line}
                            </StyledText>
                        ))}
                    </AddressBlock>
                )}
            </DetailsWrapper>
        );
    };

    return (
        <Container $grow={1} column className="order-info-wrapper">
            <Text as="label" xBold fontSize={11} className="mb-1" padding={0} margin={0}>
                {t('orderInformation')}
            </Text>
            <Wrapper column>
                {hasData ? (
                    <Flex column gap="0.5rem">
                        {orderDeliveryInfo.map((order, index) => {
                            const isExpanded = expandedOrders.has(index);
                            const showToggle = hasDetails(order);

                            return (
                                <OrderWrapper key={`order-${index}`} column className="">
                                    <OrderHeader
                                        type="button"
                                        onClick={() => showToggle && toggleOrder(index)}
                                        aria-expanded={isExpanded}
                                        $clickable={showToggle}
                                    >
                                        <BolderText as="label">
                                            {order.orderNumber}
                                            <Text fontSize={12} thin as="span">
                                                {order.orderDate && ` (${formatDate(order.orderDate)})`}
                                            </Text>
                                        </BolderText>
                                        {showToggle && (
                                            <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                                        )}
                                    </OrderHeader>
                                    {isExpanded && showToggle && renderOrderDetails(order)}
                                </OrderWrapper>
                            );
                        })}
                    </Flex>
                ) : (
                    <StyledText as="span">{t('noOrderInformation')}</StyledText>
                )}
            </Wrapper>
        </Container>
    );
};

export default OrderInfoCard;

const Container = styled(Flex)``;

const Wrapper = styled(Flex)`
    height: 100%;
    background-color: ${({ theme }) => theme.colors.fillWhite};
    padding: 1rem;
    border-radius: var(--radius);
`;

const BolderText = styled(Text)`
    font-size: 12px;
    font-weight: 600;
`;

const StyledText = styled(Text)`
    font-size: 12px;
`;

const OrderWrapper = styled(Flex)`
    gap: 0.2rem;
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight ?? '#eee'};
    padding-bottom: 0.5rem;

    &:last-child {
        border-bottom: none;
        padding-bottom: 0;
    }
`;

const OrderHeader = styled.button<{ $clickable: boolean }>`
    align-items: center;
    background: none;
    border: none;
    color: inherit;
    cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
    display: flex;
    justify-content: space-between;
    padding: 0;
    width: 100%;
`;

const DetailsWrapper = styled(Flex)`
    gap: 0.2rem;
    padding-left: 0.5rem;
    margin-top: 0.25rem;
`;

const AddressBlock = styled(Flex)`
    gap: 0.1rem;
    margin-top: 0.25rem;
`;
